<template>
  <div class="background">
    <div class="rainbow-box">
      <i class="color-block color-1" />
      <i class="color-block color-2" />
      <i class="color-block color-3" />
      <i class="color-block color-4" />
      <i class="color-block color-5" />
      <i class="color-block color-6" />
    </div>
    <div class="main-box">
      <div class="logo">
        <el-image :src="chatInfo.icon" />
      </div>
      <div
        ref="chatListBox"
        class="chat-list"
        @dragover.prevent
        @dragenter.prevent
        @drop="handleFileDrop"
      >
        <div class="chat-box answer">
          <el-image class="chat-avatar" :src="chatInfo.icon" />
          <div class="chat-content">{{ chatInfo.prologue }}</div>
        </div>
        <template v-for="(item, index) in chatRecords">
          <div v-if="item.type === 'question'" :key="index" class="chat-box question">
            <template v-if="item.files.length > 0">
              <div v-for="file in item.files" :key="file.file_id" class="chat-file">
                <p class="file-name">{{ file.file_name }}</p>
                <p class="file-info">{{ file.file_ext }} · {{ file.file_size }}</p>
              </div>
              <div class="break" />
            </template>
            <div v-if="translateLoading && index === chatRecords.length - 1" class="chat-content">
              <div class="translate-loading">
                <div><i /><i /><i /></div>
              </div>
            </div>
            <div v-else class="chat-content">{{ item.content }}</div>
          </div>
          <div v-if="item.type === 'answer'" :key="index" class="chat-box answer">
            <el-image class="chat-avatar" :src="chatInfo.icon" />
            <div class="chat-content">
              <div v-if="showLoading && index === chatRecords.length - 1" class="think-loading" @click="toggleThinkDom(index)">
                <div>正在思考<i /><i /><i /></div>
              </div>
              <div v-if="chatInfo.is_think && !showLoading" class="finish-loading" @click="toggleThinkDom(index)">
                <span>思考结束</span>
                <i v-if="!showThinkStatus[index]" class="el-icon-arrow-down" />
                <i v-else class="el-icon-arrow-up" />
              </div>
              <div :id="`answer-${index}`" v-html="item.formatContent" />
            </div>
            <div class="operation-box">
              <i class="el-icon-document-copy" @click="copyContent(item.content)" />
            </div>
          </div>
        </template>
      </div>
      <div class="chat-options">
        <div class="option-item" :class="{ 'active': searchEnabled }" @click="changeSearchEnable()">
          <el-image :class="searchEnabled ? 'search-btn active' : 'search-btn'" :src="searchEnabled ? SearchIconWhite : SearchIconBlue" />
          <span>联网搜索</span>
        </div>
        <div v-if="useDigitalPerson && isSmallDevice" class="option-item" @click="redirectToCall">
          <el-image :src="CallIcon" />
          <span>打电话</span>
        </div>
      </div>
      <div class="chat-input-box">
        <div v-if="inputType === 'text'" class="input-outline" :class="{ 'drag-over': isDragOver }" @dragover.prevent="handleDragOver" @dragleave="handleDragLeave" @drop="handleInputDrop">
          <div class="input-box">
            <div v-if="uploadedFile.length > 0" class="files-box">
              <div v-for="item in uploadedFile" :key="item.file_id" class="file-item" @click="removeFile">
                <p class="file-name">{{ item.file_name }}</p>
                <p class="file-info">{{ item.file_ext }} · {{ item.file_size }}</p>
                <i class="el-icon-error close-icon" />
              </div>
            </div>
            <div class="input-container">
              <el-image class="switch-icon" :src="VoiceIcon" @click="switchInputType('voice')" />
              <el-input
                v-model="inputContent"
                type="textarea"
                :placeholder="fileUploading ? '正在处理文件...' : '请输入您的问题'"
                :autosize="{ minRows: 1, maxRows: 4 }"
                resize="none"
                :disabled="fileUploading"
              />
              <div v-if="fileUploading" class="file-uploading">
                <i class="el-icon-loading" />
                <span>文件处理中...</span>
              </div>
              <el-tooltip class="item" effect="dark" content="上传附件（仅识别文字）" placement="top">
                <el-image class="search-btn" :src="AttachmentIcon" @click="triggerFileUpload" />
              </el-tooltip>
              <input
                ref="fileInput"
                type="file"
                accept=".jpg,.jpeg,.png,.txt,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.pdf"
                style="display: none"
                @change="handleFileSelect"
              >
              <el-image v-if="generating" class="send-btn active" :src="StopIcon" @click="stopGenerating" />
              <el-image v-else-if="inputContent && !generating" class="send-btn" :class="{ 'active': inputContent }" :src="SendIconWhite" @click="sendMessage()" />
              <el-image v-else class="send-btn" :class="{ 'active': inputContent }" :src="SendIconBlue" />
            </div>
          </div>
        </div>
        <div
          v-if="inputType === 'voice'"
          class="voice-outline"
          @mouseup="stopRecord"
          @touchend="stopRecord"
          @mouseleave="mouseLeaveArea"
          @mousemove="touchOrClickMove"
          @touchmove="touchOrClickMove"
        >
          <div v-show="showVoiceStatus" class="voice-display">
            <div class="voice-tips">我在听，请说话</div>
            <div class="voice-operation" :style="{ color: voiceCancel ? '#FF8282' : '#84888B' }">
              <span v-if="voiceCancel">松开取消</span>
              <span v-else>松开发送， 上滑取消</span>
            </div>
            <div ref="voiceWave" class="voice-wave" style="width: 100%; height: 40px;" />
          </div>
          <div class="voice-box">
            <el-image class="switch-icon" :src="KeyboardIcon" @click="switchInputType('text')" />
            <div
              class="voice-btn"
              @mousedown="startRecord"
              @touchstart="startRecord"
            >按住说话</div>
            <el-image v-if="true" class="send-btn" :class="{ 'active': generating }" :src="StopIcon" @click="stopGenerating" />
          </div>
        </div>
        <div class="content-tips">内容由AI大模型生成，仅供参考。</div>
      </div>
      <!-- 数字人视频及音频 -->
      <div v-if="useDigitalPerson" class="digital-person">
        <img v-if="digitalPersonSpeaking" :src="SpeakDigitalPerson" class="jsmpeg">
        <img v-else :src="NoSpeakDigitalPerson" class="jsmpeg">
      </div>
      <audio ref="audio" :src="currentVoice" preload="auto" />
    </div>
  </div>
</template>

<script>
import Recorder from 'recorder-core';
import 'recorder-core/src/engine/mp3';
import 'recorder-core/src/engine/mp3-engine';
import 'recorder-core/src/extensions/waveview';
import markdownit from 'markdown-it';
import { getChatInfo } from '@/api/customer';
import { configFile } from '@/utils/constant';
import RobotIcon from "@/assets/images/icons/robots/robot.webp";
import ImageUploadIcon from "@/assets/images/icons/robots/image_upload.svg";
import SearchIconBlue from "@/assets/images/icons/robots/search_blue.svg";
import SearchIconWhite from "@/assets/images/icons/robots/search_white.svg";
import SendIconBlue from "@/assets/images/icons/robots/sent_blue.svg";
import SendIconWhite from "@/assets/images/icons/robots/sent_white.svg";
import StopIcon from "@/assets/images/icons/robots/stop.svg";
import KeyboardIcon from "@/assets/images/icons/robots/keyboard.svg";
import VoiceIcon from "@/assets/images/icons/robots/voice.svg";
import CallIcon from "@/assets/images/icons/robots/voice-call.svg";
import AttachmentIcon from '@/assets/images/icons/robots/attachment.svg';
import SpeakDigitalPerson from '@/assets/videos/robot/model-meg-talking.gif';
import NoSpeakDigitalPerson from '@/assets/videos/robot/model-meg.gif';

export default {
  data() {
    return {
      RobotIcon,
      ImageUploadIcon,
      StopIcon,
      KeyboardIcon,
      VoiceIcon,
      CallIcon,
      AttachmentIcon,
      SearchIconBlue,
      SearchIconWhite,
      SendIconBlue,
      SendIconWhite,
      SpeakDigitalPerson,
      NoSpeakDigitalPerson,
      isSmallDevice: window.innerWidth < 768,
      stopFlag: true,
      showLoading: false,
      generating: false,
      translateLoading: true,
      agentId: '',
      chatInfo: {},
      inputType: 'text',
      showVoiceStatus: false,
      voiceDuration: 0,
      hadVoiceInput: false,
      voiceCancel: false,
      voiceInstance: null,
      wave: null,
      mouseMoveStartY: 0,
      inputContent: '',
      currentQuestion: '',
      searchEnabled: 0,
      chatRecords: [],
      showThinkStatus: [],
      useDigitalPerson: false,
      digitalPersonSpeaking: false,
      currentVoice: '',
      voiceList: [],
      uploadedFile: [],
      currentFile: [],
      fileUploading: false,
      isDragOver: false,
    }
  },
  watch: {
    chatRecords: {
      deep: true,
      handler() {
        const md = markdownit({
          html: true,
          breaks: true,
        });
        this.chatRecords[this.chatRecords.length - 1].formatContent = md.render(this.chatRecords[this.chatRecords.length - 1].content);
      }
    },
    voiceList: {
      deep: true,
      handler(val) {
        if (this.currentVoice === '') {
          // 播放语音
          this.playVoiceList();
        } else {
          // 预加载下一条语音
          if (val.length > 1) {
            const audio = document.createElement('audio');
            audio.src = val[0];
            audio.preload = 'auto';
            console.log('预加载下一条语音', audio.src);
            audio.load();
          }
        }
      }
    }
  },
  mounted() {
    document.title = '智能小助手';
    this.agentId = this.$route.params.id;
    if (!this.agentId) {
      this.$router.replace('/404')
    }
    this.getChatInfo();
    this.$refs.audio.addEventListener('ended', this.playVoiceList)
  },
  beforeDestroy() {
    this.$refs.audio.removeEventListener('ended', this.playVoiceList);
  },
  methods: {
    playVoiceList() {
      this.currentVoice = '';
      if (this.voiceList.length > 0) {
        this.currentVoice = this.voiceList[0];
        this.voiceList.shift();
        this.$nextTick(() => {
          this.digitalPersonSpeaking = true;
          this.$refs.audio.play();
        });
      } else {
        this.digitalPersonSpeaking = false;
      }
    },
    stopVoice() {
      this.$refs.audio.pause();
      this.$refs.audio.currentTime = 0;
      this.currentVoice = '';
      this.digitalPersonSpeaking = false;
      this.voiceList = [];
    },
    async getChatInfo() {
      const { data } = await getChatInfo({ agent_id: this.agentId });
      if (data.chat_id) {
        document.title = data.name;
        this.chatInfo = data;
        this.useDigitalPerson = data.is_digital;
      } else {
        this.$router.replace('/404')
      }
    },
    switchInputType(type) {
      this.inputType = type;
      if (type === 'voice') {
        this.initRecorder();
      } else {
        this.voiceInstance.close();
        this.voiceInstance = null;
      }
    },
    // 构造录音实例
    initRecorder() {
      this.voiceInstance = Recorder({
        type: 'mp3',
        sampleRate: 16000,
        bitRate: 16,
        onProcess: (buffers, powerLevel, bufferDuration, bufferSampleRate) => {
          this.voiceDuration = bufferDuration;
          if (powerLevel > 10) this.hadVoiceInput = true;
          this.wave.input(buffers[buffers.length - 1], powerLevel, bufferSampleRate);
        }
      });
      // 请求麦克风权限
      this.openRecorder();
    },
    // 没有权限时打开录音实例
    openRecorder() {
      this.voiceInstance.open(() => {
        setTimeout(() => {
          this.voiceInstance.close();
        }, 100);
      }, (msg, isUserNotAllow) => {
        if (isUserNotAllow) {
          this.$message.error('用户拒绝使用麦克风权限');
        } else {
          this.$message.error(msg);
        }
      });
      return;
    },
    async startRecord(e) {
      event.preventDefault();
      this.mouseMoveStartY = e.clientY || e.touches[0].clientY;
      this.showVoiceStatus = true;
      await this.$nextTick();
      this.voiceInstance.open(() => {
        this.wave = Recorder.WaveView({ elem: this.$refs.voiceWave });
        this.voiceInstance.start();
      }, (msg, isUserNotAllow) => {
        this.showVoiceStatus = false;
        if (isUserNotAllow) {
          this.$message.error('用户拒绝使用麦克风权限');
        } else {
          this.$message.error(msg);
        }
      });
    },
    touchOrClickMove(e) {
      const moveY = e.clientY || e.touches[0].clientY;
      if (this.mouseMoveStartY - moveY > 30) {
        this.voiceCancel = true;
      } else {
        this.voiceCancel = false;
      }
    },
    mouseLeaveArea() {
      if (!this.showVoiceStatus) return;
      this.voiceInstance.stop();
      this.showVoiceStatus = false;
      this.voiceCancel = false;
    },
    stopRecord() {
      if (!this.showVoiceStatus) return;
      if (this.voiceCancel) {
        this.voiceInstance.stop();
        this.showVoiceStatus = false;
        this.voiceCancel = false;
        return;
      }
      this.translateLoading = true;
      this.showVoiceStatus = false;
      this.voiceInstance.stop(async blob => {
        if (this.voiceDuration < 1000) {
          this.translateLoading = false;
          this.$message.warning('长按时间太短');
          return;
        }
        if (!this.hadVoiceInput) {
          this.translateLoading = false;
          this.$message.warning('未识别到文字');
          return;
        }
        this.chatRecords.push({
          type: 'question',
          content: ''
        });
        this.$nextTick(() => {
          this.$refs.chatListBox.scrollTop = this.$refs.chatListBox.scrollHeight;
        });
        const formData = new FormData();
        formData.append('file', blob, 'voice.mp3');
        const response = await fetch(process.env.VUE_APP_VOICE2TEXT_API, {
          method: 'POST',
          body: formData
        })
        this.translateLoading = false;
        if (!response.ok) {
          this.chatRecords[this.chatRecords.length - 1].content = '服务繁忙，请稍后重试。';
          this.$nextTick(() => {
            this.$refs.chatListBox.scrollTop = this.$refs.chatListBox.scrollHeight;
          });
          return;
        }
        const reader = response.body.getReader();
        const { value } = await reader.read();
        const textDecoder = new TextDecoder('utf-8');
        const result = JSON.parse(textDecoder.decode(value));
        this.chatRecords.pop();
        if (!result.data.text || result.data.text.trim().length < 1) {
          this.$message.warning('未识别到文字');
          return;
        }
        this.inputContent = result.data.text;
        this.sendMessage();
      }, (err) => {
        this.translateLoading = false;
        console.log('录音错误：' + err);
      });
    },
    async stopGenerating() {
      if (!this.generating) return;
      if (this.useDigitalPerson) {
        this.stopVoice();
      }
      this.stopFlag = true;
      this.showLoading = false;
      this.inputContent = '';
      this.generating = false;
      await fetch(process.env.VUE_APP_STOP_CHAT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chat_id: this.chatInfo.chat_id,
          prompt: this.currentQuestion,
        })
      });
      this.chatRecords.push({
        type: 'answer',
        content: '用户已停止生成。',
        formatContent: '用户已停止生成。'
      });
      this.$nextTick(() => {
        this.$refs.chatListBox.scrollTop = this.$refs.chatListBox.scrollHeight;
      });
    },
    sendMessage() {
      if (this.useDigitalPerson) {
        this.stopVoice();
      }
      this.stopFlag = false;
      this.chatRecords.push({
        type: 'question',
        content: this.inputContent,
        files: this.uploadedFile
      });
      this.currentQuestion = this.inputContent;
      this.currentFile = this.uploadedFile;
      this.inputContent = '';
      this.uploadedFile = [];
      this.showLoading = true;
      if (!this.stopFlag) {
        this.chatRecords.push({
          type: 'answer',
          content: '',
          formatContent: ''
        });
      }
      this.$nextTick(() => {
        this.$refs.chatListBox.scrollTop = this.$refs.chatListBox.scrollHeight;
      });
      this.useFetchRequest();
      // 如果未启用数字人，则使用fetch请求，如果启用数字人，则使用websocket请求
      // if (!this.useDigitalPerson) {
      //   this.useFetchRequest();
      // } else {
      //   this.useWebSocketRequest();
      // }
    },
    // 使用Fetch请求的方式
    async useFetchRequest() {
      this.generating = true;
      const response = await fetch(process.env.VUE_APP_AI_CHAT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        responseType: 'stream',
        body: JSON.stringify({
          agent_id: this.agentId,
          chat_id: this.chatInfo.chat_id,
          prompt: this.currentQuestion,
          file_id: this.currentFile.length > 0 ? this.currentFile[0].file_id : '',
          search: this.searchEnabled ? 1 : 0
        })
      });
      if (!response.ok) {
        this.chatRecords[this.chatRecords.length - 1].content = '服务繁忙，请稍后重试。';
        this.$nextTick(() => {
          this.$refs.chatListBox.scrollTop = this.$refs.chatListBox.scrollHeight;
        });
        return;
      }
      this.$set(this.showThinkStatus, this.chatRecords.length - 1, true);
      this.showLoading = false;
      const reader = response.body.getReader();
      const textDecoder = new TextDecoder('utf-8');
      let result = true;
      let thinkContent = false;
      while (result) {
        const { done, value } = await reader.read();
        if (done || this.stopFlag) {
          result = false;
          break;
        }
        textDecoder.decode(value).split('\n').forEach(item => {
          if (!item) {
            return;
          }
          const resultStr = item?.replace("data:", '').replaceAll(' ', ' ') || '';
          const resultObh = JSON.parse(resultStr);
          // 讲声音源插入音频列表中
          if (this.useDigitalPerson && resultObh.voice) {
            this.voiceList.push(resultObh.voice);
          }
          // 获取文字内容
          const text = resultObh.answer;
          if (!this.chatInfo.is_think) {
            if (text.includes('</think>')) {
              thinkContent = false;
            }
            if (thinkContent) return;
            if (text.includes('<think>')) {
              thinkContent = true;
              return;
            }
          }
          const formatContent = text.replace('<think>', '<div class="think">').replace('</think>', '</div>');
          this.chatRecords[this.chatRecords.length - 1].content += formatContent;
          this.$nextTick(() => {
            this.$refs.chatListBox.scrollTop = this.$refs.chatListBox.scrollHeight;
          });
        })
      }
      this.generating = false;
    },
    // 使用Websocket请求的方式
    useWebSocketRequest() {
      console.log('websocket请求');
    },
    async copyContent(text) {
      try { // 浏览器支持 navigator.clipboard.writeText 方法
        await navigator.clipboard.writeText(text);
        this.$message.success('复制成功');
      } catch (err) { // 浏览器不支持 navigator.clipboard.writeText 方法
        const input = document.createElement('textarea');
        input.value = text;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        this.$message.success('复制成功');
      }
    },
    toggleThinkDom(idx) {
      const answerDom = document.getElementById(`answer-${idx}`);
      const thinkDom = answerDom.querySelector('.think');
      if (thinkDom.style.display === 'none') {
        thinkDom.style.display = 'block';
        this.$set(this.showThinkStatus, idx, true);
      } else {
        thinkDom.style.display = 'none';
        this.$set(this.showThinkStatus, idx, false);
      }
    },
    changeSearchEnable() {
      if (this.uploadedFile.length > 0) {
        this.$message.warning('解析文件时，联网搜素功能暂不可用。')
        this.searchEnabled = 0;
        return;
      }
      this.searchEnabled = !this.searchEnabled;
    },
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    handleFileDrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },
    async processFile(file) {
      // 检查文件大小（限制为2MB，根据配置）
      // const maxSize = configFile.fileupload_size * 1024; // KB转换为字节
      // if (file.size > maxSize) {
      //   this.$message.error(`文件大小不能超过${configFile.fileupload_size}KB`);
      //   return;
      // }

      // 检查文件类型
      const allowedExts = configFile.aichat_ext.split(',');
      const fileExt = file.name.split('.').pop().toLowerCase();
      if (!allowedExts.includes(fileExt)) {
        this.$message.error(`不支持的文件类型，支持的格式：${configFile.aichat_ext}`);
        return;
      }
      this.fileUploading = true;
      try {
        // 上传文件到服务器
        const result = await this.uploadFileToServer(file);
        // 解析文件时，联网搜索不可用。
        if (this.searchEnabled) {
          this.$message.warning('解析文件时，联网搜素功能暂不可用。')
          this.searchEnabled = 0;
        }
        const fileName = result.data.file_name;
        result.data.file_ext = fileName.split('.').pop().toUpperCase();
        this.uploadedFile = [result.data];
      } catch (error) {
        this.$message.error('文件上传失败，请重试');
        console.error('文件上传错误:', error);
      } finally {
        this.fileUploading = false;
        // 清空文件输入框
        this.$refs.fileInput.value = '';
      }
    },
    async uploadFileToServer(file) {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(process.env.VUE_APP_AI_UPLOAD_API, {
        method: 'POST',
        body: formData
      });
      if (!response.ok) {
        throw new Error(`上传失败: ${response.status} ${response.statusText}`);
      }
      const result = await response.json();
      console.log(result);
      return result;
    },
    handleDragOver(event) {
      event.preventDefault();
      this.isDragOver = true;
    },
    handleDragLeave(event) {
      event.preventDefault();
      this.isDragOver = false;
    },
    handleInputDrop(event) {
      event.preventDefault();
      this.isDragOver = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },
    removeFile() {
      this.uploadedFile = [];
    },
    redirectToCall() {
      this.$router.push(`/m/robot/voicecall/${this.agentId}`);
    }
  }
}
</script>

<style lang="less" scoped>
.background {
  width: 100%;
  height: 100%;
  min-height: 100vh;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;

  @media screen and (max-width: 768px) {
    padding: 0;
  }

  .rainbow-box {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    user-select: none;

   .color-block {
      position: absolute;
      filter: blur(16vw);
    }

   .color-1 {
      top: 5%;
      left: 60%;
      width: 20vw;
      height: 20vw;
      background-color: #5140EC;
    }

   .color-2 {
      top: 5%;
      left: 5%;
      width: 20vw;
      height: 20vw;
      background-color: #B2E9E9;
    }

   .color-3 {
      top: 30%;
      left: 30%;
      width: 40vw;
      height: 40vw;
      background-color: #FFDCDA;
    }

   .color-4 {
      right: 5%;
      bottom: 5%;
      width: 20vw;
      height: 20vw;
      background-color: #B2E9E9;
    }
    .color-5 {
      left: 5%;
      bottom: 5%;
      width: 20vw;
      height: 20vw;
      background-color: #5140EC;
    }
    .color-6 {
      top: 0;
      right: 0;
      width: 20vw;
      height: 20vw;
      background-color: #FFDCDA;
    }
  }

  .main-box {
    position: relative;
    width: 100%;
    max-width: 1200px;
    margin-top: 40px;
    height: calc(100vh - 160px);
    min-height: 600px;
    background-color: #fff;
    border-radius: 50px;
    box-shadow: 0 0 50px #6E63EF33;
    padding: 64px;
    padding-right: 32px;
    display: flex;
    flex-direction: column;

    @media screen and (max-width: 768px) {
      height: calc(100vh - 60px);
      min-height: auto;
      margin-top: 60px;
      padding: 48px 16px 16px;
      border-radius: 30px 30px 0 0;
    }

    .logo {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100px;
      height: 100px;
      border-radius: 50%;
      overflow: hidden;
    }

    .chat-list {
      width: 100%;
      flex: 1;
      padding: 0 32px 40px 0;
      overflow-y: auto;
      clip-path: inset(0 32px 0 0);
      z-index: 1;
      opacity: 0.8;

      @media screen and (max-width: 768px) {
        padding: 0 16px 0 0;
        clip-path: inset(0 16px 0 0);
      }

      .chat-box {
        display: flex;

        &.answer {
          padding-bottom: 40px;
          justify-content: flex-start;

          @media screen and (max-width: 768px) {
            margin-left: 0;
          }

          .chat-content {
            background-color: #F6F6F6;

            ::v-deep {
              div.think {
                border-left: 2px solid #e5e5e5;
                padding-left: 12px;
                color: #84888B;
                font-size: calc(1em - 2px);
                margin-bottom: 20px;
              }
              p {
                white-space: pre-wrap;
              }
              ol {
                padding-left: 24px;
                list-style-type: decimal;
              }
              ul {
                padding-left: 20px;
                list-style-type: disc;
              }
              li {
                overflow-wrap: break-word;
              }
              pre {
                padding: 16px;
                background-color: #333333;
                color: #ffffff;
                border-radius: 6px;
                word-break: break-all;
              }
              code {
                white-space: pre-wrap;
              }
              table {
                border-collapse: collapse;
                background-color: #FFFFFF;

                thead {
                  background-color: #e6e6e6;
                }

                th, td {
                  padding: 4px;
                  border: 1px solid #e0e0e0;
                }
              }
            }

            &::before {
              content: "";
              position: absolute;
              top: 20px;
              left: -6px;
              width: 12px;
              height: 12px;
              transform: rotate(45deg);
              background-color: #F6F6F6;

              @media screen and (max-width: 768px) {
                top: 12px;
              }
            }

            &.loading {
              display: flex;
              align-items: center;

              i {
                display: inline-block;
                width: 6px;
                height: 6px;
                border-radius: 100%;
                background-color: #5645F4;
                margin: 0 2px;
                &:nth-child(1) {
                  margin-left: 10px;
                  animation: bounce 1s ease-in-out infinite;
                }
                &:nth-child(2) {
                  animation: bounce 1s ease-in-out 0.33s infinite;
                }
                &:nth-child(3) {
                  animation: bounce 1s ease-in-out 0.66s infinite;
                }
              }
            }
          }
        }
        &.question {
          justify-content: flex-end;
          flex-wrap: wrap;

          .chat-file {
            max-width: 200px;
            background-color: #F5F5F5;
            padding: 8px 16px;
            border-radius: 6px;
            margin-bottom: -8px;

            .file-name {
              font-size: 14px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .file-info {
              font-size: 12px;
              color: #ABABAB;
            }
          }

          .break {
            flex-basis: 100%;
          }

          .chat-content {
            background-color: #5645F4;
            color: #fff;

            .think-loading {
              display: flex;
              align-items: center;
              width: fit-content;
              font-size: calc(1em - 2px);

              i {
                display: inline-block;
                width: 6px;
                height: 6px;
                border-radius: 100%;
                background-color: #5645F4;
                margin: 0 2px;
                &:nth-child(1) {
                  margin-left: 10px;
                  animation: bounce 1s ease-in-out infinite;
                }
                &:nth-child(2) {
                  animation: bounce 1s ease-in-out 0.33s infinite;
                }
                &:nth-child(3) {
                  animation: bounce 1s ease-in-out 0.66s infinite;
                }
              }
            }
          }
        }

        .chat-avatar {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          margin-right: 16px;
          margin-bottom: -14px;

          @media screen and (max-width: 768px) {
            width: 40px;
            height: 40px;
            margin-top: 10px;
          }
        }

        .chat-content {
          width: fit-content;
          height: fit-content;
          font-size: 16px;
          max-width: 65%;
          margin-top: 12px;
          padding: 16px;
          border-radius: 16px;
          background-color: #fff;

          @media screen and (max-width: 768px) {
            max-width: 80%;
            padding: 8px;
            border-radius: 8px;
            font-size: 14px;
          }

          .translate-loading {
            display: flex;
            align-items: center;
            width: fit-content;
            font-size: calc(1em - 2px);

            i {
              display: inline-block;
              width: 6px;
              height: 6px;
              border-radius: 100%;
              background-color: #FFFFFF;
              margin: 0 2px;
              &:nth-child(1) {
                animation: bounce 1s ease-in-out infinite;
              }
              &:nth-child(2) {
                animation: bounce 1s ease-in-out 0.33s infinite;
              }
              &:nth-child(3) {
                animation: bounce 1s ease-in-out 0.66s infinite;
              }
            }
          }

          .finish-loading {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 16px;
            width: fit-content;
            font-size: calc(1em - 2px);
            padding: 8px 16px;
            background-color: #e1e1e1;
            border-radius: 40px;
          }
        }

        .operation-box {
          position: absolute;
          bottom: 8px;
          left: 112px;
          display: flex;
          align-items: center;
          gap: 12px;

          @media screen and (max-width: 768px) {
            left: 64px;
          }

          i {
            cursor: pointer;
            font-size: 18px;
          }
        }
      }
    }

    .chat-options {
      width: 100%;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 8px;
      z-index: 20;

      .option-item {
        padding: 8px 16px;
        display: flex;
        align-items: center;
        gap: 4px;
        border-radius: 20px;
        background-color: #FFFFFF;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        cursor: pointer;

        &.active {
          background-color: #5645F4;
          color: #FFFFFF;
        }
      }
    }

    .chat-input-box {
      position: relative;
      bottom: 0;
      width: 100%;
      z-index: 2;

      @media screen and (max-width: 768px) {
        width: 100%;
      }

      .input-outline {
        width: calc(100% - 32px);
        background: linear-gradient(80deg, #26B1B1, #FF8282, #715CE0);
        border-radius: 12px;
        padding: 2px;

        @media screen and (max-width: 768px) {
          width: 100%;
        }

        &.drag-over {
          background: linear-gradient(80deg, #26B1B1, #FF8282, #715CE0);
          box-shadow: 0 0 20px rgba(113, 92, 224, 0.5);
          transform: scale(1.02);
          transition: all 0.3s ease;
        }

        .input-box {
          background-color: #fff;
          padding: 4px;
          width: 100%;
          border-radius: 10px;

          .files-box {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 12px 12px;

            .file-item {
              max-width: 200px;
              background-color: #F5F5F5;
              padding: 8px 16px;
              border-radius: 6px;
              cursor: pointer;

              &:hover {
                .close-icon {
                  display: block;
                }
              }

              .file-name {
                font-size: 14px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
              .file-info {
                font-size: 12px;
                color: #ABABAB;
              }
              .close-icon {
                position: absolute;
                top: -6px;
                right: -6px;
                font-size: 20px;
                color: #333333;
                display: none;
              }
            }
          }

          .input-container {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 8px;

            ::v-deep {
              .el-textarea__inner {
                border: none;
                padding: 7px 0;
                font-size: 16px;
              }
            }

            .switch-icon {
              width: 36px;
              height: 36px;
              padding: 4px 0;
              cursor: pointer;
            }

            .upload-icon {
              height: 36px;
              padding: 4px 0;
              cursor: pointer;
            }

            .send-btn {
              display: flex;
              align-items: center;
              height: 36px;
              padding: 4px 10px;
              border-radius: 8px;
              cursor: pointer;
            }
            .search-btn{
              display: flex;
              align-items: center;
              height: 36px;
              padding: 4px 8px;
              border-radius: 8px;
              cursor: pointer;
            }
            .active {
              background-color: #5645F4;
              cursor: pointer;
            }

            .file-uploading {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              display: flex;
              align-items: center;
              gap: 8px;
              color: #5645F4;
              font-size: 14px;
              background-color: rgba(255, 255, 255, 0.9);
              padding: 8px 16px;
              border-radius: 20px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              z-index: 10;

              i {
                font-size: 16px;
                animation: rotate 1s linear infinite;
              }
            }
          }
        }
      }

      .voice-outline {
        width: calc(100% - 32px);
        position: relative;
        user-select: none;

        @media screen and (max-width: 768px) {
          width: 100%;
        }

        &::before {
          content: "";
          position: absolute;
          bottom: 0;
          left: -20px;
          width: calc(100% + 40px);
          height: 100%;
          background: linear-gradient(80deg, #26B1B1, #FF8282, #715CE0);
          opacity: 0.8;
          filter: blur(30px);
        }

        .voice-display {
          padding: 16px;
          margin-bottom: 2px;
          border-radius: 8px;
          background-color: #FFFFFF;

          .voice-tips {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 8px;
          }
          .voice-operation {
            font-size: 16px;
            color: #84888B;
            margin-bottom: 8px;
            text-align: center;
          }
          .voice-wave-l {
            margin-top: 24px;
            width: 100%;
            height: 20px;
            background-image: linear-gradient(to right, #FFFFFF, transparent, #FFFFFF), url('~@/assets/images/icons/robots/voice_l.svg');
            background-position: 0, 0;
            background-repeat: no-repeat, repeat-x;
            background-size: 100% 100%, 52px 100%;
            animation: wave 0.5s infinite linear;
          }
          .voice-wave-n {
            margin-top: 36px;
            width: 100%;
            height: 8px;
            background-image: linear-gradient(to right, #FFFFFF, transparent, #FFFFFF), url('~@/assets/images/icons/robots/voice_n.svg');
            background-position: 0, 0;
            background-repeat: no-repeat, repeat-x;
            background-size: 100% 100%, 56px 100%;
          }
        }

        .voice-box {
          width: 100%;
          padding: 8px;
          position: relative;
          border-radius: 8px;
          display: flex;
          align-items: center;
          background-color: #FFFFFF;

          .switch-icon {
            width: 36px;
            height: 36px;
            padding: 4px 0;
            margin-right: 12px;
            cursor: pointer;
          }

          .voice-btn {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
          }

          .send-btn {
            display: flex;
            align-items: center;
            height: 36px;
            padding: 4px 10px;
            border-radius: 8px;
          }
          .active {
            background-color: blue;
            cursor: pointer;
          }
        }
      }

      .content-tips {
        width: 100%;
        text-align: center;
        margin-top: 8px;
        font-size: 12px;
        color: #84888B;
        border-radius: 6px;
      }
    }

    .digital-person {
      position: absolute;
      left: calc(100% - 138px - 60px);
      bottom: 136px;
      width: 136px;
      height: 256px;
      z-index: 0;

      @media screen and (max-width: 768px) {
        left: calc(50% - 68px);
        bottom: 80px;
      }

      video {
        width: 100%;
        height: 100%;
      }

      .jsmpeg {
        width: 100%;
        height: 100%;
      }
    }
  }
}
@keyframes bounce {
  0%, 75%, 100% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(-6px)
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes wave {
  0% {
    background-position: 0, 0;
  }
  100% {
    background-position: 0, -52px;
  }
}
</style>
